/* eslint-disable complexity */
import {
    RealtyObjectType, Renovation, Rooms, DealType
} from '@search/filter-enums/enums';
import { GeoKind } from '@search/filter-enums/enums/GeoKind';
import { BreadcrumbSection } from '@search/vtbeco-ui/src/components/Breadcrumb';

import { SearchRoute, useRouter } from '@search/offer-search-routes/src';
import { RegionIdEnum } from '@search/filter-enums/enums/Region';
import { OfferSearchTextBuilder } from './text/Builder';

export type SeoGeos = {
    readonly id: number;
    readonly title: string;
    readonly titleShort?: string;
    readonly locative?: string;
    readonly locativeName?: string;
    readonly kind: any;
    readonly locality?: {
        id: number;
        title?: string;
        locative: string;
    };
}

export type OfferSearchBreadcrumbSectionsParams = {
    region: RegionIdEnum;
    dealType: DealType;
    categoryType: RealtyObjectType;
    renovation: readonly Renovation[];
    rooms: readonly Rooms[];
    geos: readonly SeoGeos[];
    title?: string;
    rootRegion?: {
        id: number;
        title?: string | null;
        locative?: string | null;
        displayName?: string | null;
    };
    offersCount?: number;
}

export function useOfferSearchBreadcrumbSections(
    searchParams: OfferSearchBreadcrumbSectionsParams,
    isIconDisabled = false
) {
    const dealType = searchParams.dealType as DealType;
    const textBuilder = new OfferSearchTextBuilder();
    const router = useRouter();

    const regionText = searchParams.rootRegion?.locative ?? '';
    let locality;
    let metro;
    let district;
    let street;

    for (const geo of searchParams.geos) {
        if (geo.kind === GeoKind.METRO || geo.kind === 'METRO') metro = geo;
        if (geo.kind === GeoKind.DISTRICT || geo.kind === 'DISTRICT') district = geo;
        if (geo.kind === GeoKind.STREET || geo.kind === 'STREET') street = geo;
        if (geo.kind === GeoKind.LOCALITY || geo.kind === 'LOCALITY') locality = geo;
    }

    const route = router.route(SearchRoute);

    const contentDealTypeBreadcrumb = textBuilder.dealAndCategory({
        dealType,
        categoryType: searchParams.categoryType,
        regionText
    });
    const iconContent = isIconDisabled ? '' : textBuilder.seoIconCategory({
        categoryType: searchParams.categoryType
    });

    const sections: BreadcrumbSection[] = [
        {
            key: 'main',
            href: '/',
            content: 'Главная'
        },
        {
            key: 'dealType',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType
            }),
            content: contentDealTypeBreadcrumb,
            contentSeo: iconContent + contentDealTypeBreadcrumb
        }
    ];

    const baseParameters = {
        region: searchParams.region,
        dealType,
        categoryType: searchParams.categoryType,
        rooms: searchParams.rooms
    } as const;

    if (searchParams.rooms.length === 1) {
        sections.push({
            key: 'rooms',
            href: route.url(baseParameters),
            content: textBuilder.roomType({ rooms: searchParams.rooms })
        });
    }

    if (locality) {
        sections.push({
            key: 'localityIds',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType,
                districtIds: [ locality.id ]
            }),
            content: locality.locative
        });
    }

    if (metro) {
        sections.push({
            key: 'metroIds',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType,
                metroIds: [ metro.id ]
            }),
            content: textBuilder.metro({ metro })
        });
    }

    if (district && ! isIconDisabled) {
        sections.push({
            key: 'districtIds',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType,
                districtIds: [ district.id ]
            }),
            content: textBuilder.district({ district })
        });
    }

    if (street) {
        const parent = street.locality;

        if (parent && searchParams.rootRegion?.id !== parent?.id) {
            sections.push({
                key: 'streetParentsIds',
                href: route.url({
                    region: searchParams.region,
                    dealType,
                    categoryType: searchParams.categoryType,
                    districtIds: [ parent.id ]
                }),
                content: parent?.title ?? parent.locative
            });
        }

        sections.push({
            key: 'streetIds',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType,
                addresses: [ street.id ]
            }),
            content: street.locative
        });
    }

    if (searchParams.renovation.length === 1) {
        sections.push({
            key: 'renovation',
            href: route.url({
                region: searchParams.region,
                dealType,
                categoryType: searchParams.categoryType,
                renovation: searchParams.renovation
            }),
            content: textBuilder.renovation({ renovation: searchParams.renovation })
        });
    }

    if (searchParams.title) {
        sections.push({
            key: 'title',
            href: '',
            content: searchParams.title
        });
    }

    return sections;
}
